import React, { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';

const DISMISS_KEY = 'oc_pwa_dismiss_until';
const DISMISS_DAYS = 14;
const IOS_SHOWN_KEY = 'oc_pwa_ios_shown';

const isIOS = () =>
  typeof navigator !== 'undefined' &&
  /iPad|iPhone|iPod/.test(navigator.userAgent) &&
  !window.MSStream;

const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(display-mode: standalone)').matches ||
         window.navigator.standalone === true;
};

const dismissedRecently = () => {
  try {
    const until = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    return until && Date.now() < until;
  } catch { return false; }
};

const remember = (key) => {
  try { localStorage.setItem(key, '1'); } catch { /* ignore */ }
};

const InstallBanner = ({ appName = 'Obsidian Driver' }) => {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIOSHelp, setShowIOSHelp] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (dismissedRecently()) return;

    // Android/Chromium path — capture the install prompt
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', () => setVisible(false));

    // iOS Safari: no beforeinstallprompt — show our own hint, but only once
    if (isIOS()) {
      try {
        if (!localStorage.getItem(IOS_SHOWN_KEY)) {
          setVisible(true);
          remember(IOS_SHOWN_KEY);
        }
      } catch { /* ignore */ }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
    };
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_DAYS * 86400000));
    } catch { /* ignore */ }
    setVisible(false);
    setShowIOSHelp(false);
  };

  const install = async () => {
    if (deferred) {
      try {
        deferred.prompt();
        await deferred.userChoice;
      } catch { /* user cancelled */ }
      setDeferred(null);
      setVisible(false);
      return;
    }
    if (isIOS()) {
      setShowIOSHelp(true);
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-sm z-40">
      <div className="bg-white text-black rounded-2xl shadow-2xl border border-neutral-200 p-4 flex items-start gap-3" data-testid="pwa-install-banner">
        <div className="h-10 w-10 rounded-xl bg-black text-white flex items-center justify-center shrink-0 font-bold text-lg">
          O
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">Install {appName}</p>
          <p className="text-xs text-neutral-600 mt-0.5">
            Add to your home screen for one-tap access and offline support.
          </p>
          {showIOSHelp && (
            <p className="text-[11px] text-neutral-700 mt-2 leading-relaxed">
              Tap <span className="inline-block px-1 py-0.5 rounded bg-neutral-100 font-mono">Share ▴</span> in Safari, then choose <strong>Add to Home Screen</strong>.
            </p>
          )}
          <div className="flex gap-2 mt-3">
            <button
              onClick={install}
              className="inline-flex items-center gap-1.5 bg-black text-white text-xs font-semibold px-3 py-1.5 rounded-full hover:bg-neutral-800"
              data-testid="pwa-install-btn"
            >
              <Download className="h-3.5 w-3.5" /> Install
            </button>
            <button
              onClick={dismiss}
              className="text-xs text-neutral-500 hover:text-black px-2"
              data-testid="pwa-dismiss-btn"
            >
              Not now
            </button>
          </div>
        </div>
        <button
          onClick={dismiss}
          className="text-neutral-400 hover:text-black p-1 shrink-0"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default InstallBanner;
